import { ContentState, Dialog, FormActions, StatusPill } from "@whitehaven/ui";
import { HomepageContentView } from "@whitehaven/homepage-view";
import { canAccessPortalArea } from "@whitehaven/permissions";
import "@whitehaven/homepage-view/styles.css";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import type {
  HomepageContent,
  HomepageContentState,
  HomepageRevisionDetail,
  HomepageRevisionHistory,
  HomepageRevisionSummary,
} from "@whitehaven/contracts/homepage";
import { HomepageVisualEditor } from "./HomepageVisualEditor";
import {
  HomepageEditorApiError,
  type HomepageEditorApiClient,
} from "./homepage-editor-api";
import {
  cloneHomepageContent,
  formatHomepageRevisionDate,
  homepageDraftChanged,
  revisionEditorLabel,
} from "./homepage-editor-model";
import {
  usePortalSession,
  type PortalSessionController,
} from "./usePortalSession";

const PortalFirebaseSignIn = lazy(() =>
  import("../auth/PortalFirebaseSignIn").then((module) => ({
    default: module.PortalFirebaseSignIn,
  })),
);

type ConfirmationRequest =
  | {
      readonly kind: "publish";
      readonly revision: HomepageRevisionDetail;
    }
  | {
      readonly kind: "restore";
      readonly revision: HomepageRevisionDetail;
    };

interface OperationMessage {
  readonly tone: "success" | "error" | "conflict";
  readonly text: string;
  readonly correlationId?: string | null;
}

function requestFailedMessage(caught: unknown): OperationMessage {
  if (caught instanceof HomepageEditorApiError) {
    return {
      tone: caught.isConflict ? "conflict" : "error",
      text: caught.isConflict
        ? "The saved homepage changed while you were editing. Your on-screen edits are preserved; reload the latest revision before trying again."
        : caught.message,
      correlationId: caught.correlationId,
    };
  }
  return {
    tone: "error",
    text: "The homepage request could not be completed.",
  };
}

function OperationFeedback({
  message,
  onReload,
}: {
  readonly message: OperationMessage | null;
  readonly onReload: () => void;
}) {
  if (!message) return null;
  return (
    <div
      className={`homepage-editor__feedback homepage-editor__feedback--${message.tone}`}
      role={message.tone === "success" ? "status" : "alert"}
      aria-live={message.tone === "success" ? "polite" : "assertive"}
    >
      <p>{message.text}</p>
      {message.correlationId && (
        <p>
          Support reference: <code>{message.correlationId}</code>
        </p>
      )}
      {message.tone === "conflict" && (
        <button type="button" onClick={onReload}>
          Reload latest revision
        </button>
      )}
    </div>
  );
}

function RevisionStatus({
  revision,
  latestRevisionId,
  publishedRevisionId,
}: {
  readonly revision: HomepageRevisionSummary;
  readonly latestRevisionId: string | null;
  readonly publishedRevisionId: string | null;
}) {
  return (
    <span className="homepage-editor__revision-statuses">
      {revision.id === publishedRevisionId && (
        <StatusPill tone="positive">Published</StatusPill>
      )}
      {revision.id === latestRevisionId && (
        <StatusPill tone="info">Latest draft</StatusPill>
      )}
      {revision.kind === "RESTORE" && (
        <StatusPill tone="warning">Restored copy</StatusPill>
      )}
    </span>
  );
}

function RevisionHistoryList({
  history,
  busy,
  canExport,
  onPreview,
  onExport,
  onLoadOlder,
}: {
  readonly history: HomepageRevisionHistory;
  readonly busy: boolean;
  readonly canExport: boolean;
  readonly onPreview: (revisionId: string) => void;
  readonly onExport: (revision: HomepageRevisionSummary) => void;
  readonly onLoadOlder: () => void;
}) {
  if (history.items.length === 0) {
    return (
      <ContentState
        kind="empty"
        title="No revisions yet"
        live={false}
        headingLevel={3}
      >
        <p>
          No placeholder content is created here. The first revision must come
          from owner-approved real homepage content.
        </p>
      </ContentState>
    );
  }

  return (
    <>
      <ol className="homepage-editor__history-list">
        {history.items.map((revision) => (
          <li key={revision.id}>
            <div>
              <p className="homepage-editor__revision-title">
                Revision {revision.revisionNumber}
              </p>
              <RevisionStatus
                revision={revision}
                latestRevisionId={history.latestRevisionId}
                publishedRevisionId={history.publishedRevisionId}
              />
              <p>
                <time dateTime={revision.createdAt}>
                  {formatHomepageRevisionDate(revision.createdAt)}
                </time>
                {" · "}
                {revisionEditorLabel(revision)}
              </p>
            </div>
            <div className="homepage-editor__history-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => onPreview(revision.id)}
              >
                Preview
              </button>
              <button
                type="button"
                disabled={busy || !canExport}
                onClick={() => onExport(revision)}
              >
                Export backup
              </button>
            </div>
          </li>
        ))}
      </ol>
      {history.nextBeforeRevisionNumber !== null && (
        <button
          type="button"
          className="homepage-editor__secondary-button"
          disabled={busy}
          onClick={onLoadOlder}
        >
          Load older revisions
        </button>
      )}
    </>
  );
}

function RevisionContentPreview({
  revision,
}: {
  readonly revision: HomepageRevisionDetail;
}) {
  return (
    <article className="homepage-editor__preview" aria-label="Revision content">
      <dl className="homepage-editor__preview-metadata">
        <div>
          <dt>Revision</dt>
          <dd>{revision.revisionNumber}</dd>
        </div>
        <div>
          <dt>Saved</dt>
          <dd>
            <time dateTime={revision.createdAt}>
              {formatHomepageRevisionDate(revision.createdAt)}
            </time>
          </dd>
        </div>
        <div>
          <dt>Editor</dt>
          <dd>{revisionEditorLabel(revision)}</dd>
        </div>
        <div>
          <dt>Revision type</dt>
          <dd>
            {revision.kind === "RESTORE" ? "Restored copy" : "Edited draft"}
          </dd>
        </div>
      </dl>
      <p className="homepage-editor__preview-note">
        This is a read-only rendering of the exact saved content. Links are
        inactive here. It uses no iframe or script, and rendering makes no
        additional or external network requests.
      </p>
      <div
        className="homepage-editor__preview-canvas"
        data-preview-network="isolated"
      >
        <HomepageContentView
          content={revision.content}
          mode="published"
          actions="inactive"
          semanticContext="embedded"
        />
      </div>
    </article>
  );
}

function mergeHistory(
  current: HomepageRevisionHistory,
  older: HomepageRevisionHistory,
): HomepageRevisionHistory {
  const known = new Set(current.items.map(({ id }) => id));
  return {
    ...older,
    items: [
      ...current.items,
      ...older.items.filter(({ id }) => !known.has(id)),
    ],
    latestRevisionId: current.latestRevisionId,
    publishedRevisionId: current.publishedRevisionId,
    lockVersion: current.lockVersion,
  };
}

function downloadBackupDocument(
  backup: Awaited<ReturnType<HomepageEditorApiClient["exportBackup"]>>,
): void {
  const document = `${JSON.stringify(backup, null, 2)}\n`;
  const url = URL.createObjectURL(
    new Blob([document], { type: "application/json" }),
  );
  const link = window.document.createElement("a");
  link.href = url;
  link.download = `whitehaven-homepage-revision-${backup.revision.revisionNumber}.json`;
  link.rel = "noopener";
  link.click();
  URL.revokeObjectURL(url);
}

function AuthenticatedHomepageEditor({
  session,
}: {
  readonly session:
    | Extract<PortalSessionController, { readonly status: "authenticated" }>
    | PortalSessionController;
}) {
  const { client } = session;
  const csrfToken = session.data?.csrfToken ?? "";
  const [contentState, setContentState] = useState<HomepageContentState | null>(
    null,
  );
  const [history, setHistory] = useState<HomepageRevisionHistory | null>(null);
  const [draft, setDraft] = useState<HomepageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<OperationMessage | null>(null);
  const [preview, setPreview] = useState<HomepageRevisionDetail | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationRequest | null>(
    null,
  );

  const handleRequestFailure = useCallback(
    (caught: unknown) => {
      const authorizationChanged =
        caught instanceof HomepageEditorApiError &&
        caught.codes.includes("CONTENT_AUTHORIZATION_CHANGED");
      if (
        caught instanceof HomepageEditorApiError &&
        (caught.isAuthenticationRequired ||
          caught.isAccessDenied ||
          authorizationChanged)
      ) {
        setContentState(null);
        setHistory(null);
        setDraft(null);
        setPreview(null);
        setConfirmation(null);
        session.retry();
        return;
      }
      setMessage(requestFailedMessage(caught));
    },
    [session.retry],
  );

  const dirty = useMemo(
    () =>
      Boolean(
        draft &&
          (!contentState?.latestRevision ||
            homepageDraftChanged(draft, contentState.latestRevision.content)),
      ),
    [contentState, draft],
  );

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [nextState, nextHistory] = await Promise.all([
        client.loadHomepage(),
        client.loadHistory(),
      ]);
      setContentState(nextState);
      setHistory(nextHistory);
      setDraft(
        nextState.latestRevision
          ? cloneHomepageContent(nextState.latestRevision.content)
          : cloneHomepageContent(nextState.baselineContent),
      );
      setPreview(null);
      setConfirmation(null);
    } catch (caught: unknown) {
      handleRequestFailure(caught);
    } finally {
      setLoading(false);
    }
  }, [client, handleRequestFailure]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (!dirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  const reloadHistory = async () => {
    const nextHistory = await client.loadHistory();
    setHistory(nextHistory);
  };

  const saveDraft = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft || !contentState || busy || !dirty) return;
    setBusy(true);
    setMessage(null);
    try {
      const next = await client.saveDraft(
        csrfToken,
        contentState.lockVersion,
        draft,
      );
      setContentState(next);
      setDraft(
        next.latestRevision
          ? cloneHomepageContent(next.latestRevision.content)
          : null,
      );
      await reloadHistory();
      setMessage({
        tone: "success",
        text: `Draft saved as revision ${next.latestRevision?.revisionNumber ?? ""}. It is not public until separately published.`,
      });
    } catch (caught: unknown) {
      handleRequestFailure(caught);
    } finally {
      setBusy(false);
    }
  };

  const loadPreview = async (revisionId: string) => {
    setPreviewLoading(true);
    setMessage(null);
    try {
      setPreview(await client.loadRevision(revisionId));
    } catch (caught: unknown) {
      handleRequestFailure(caught);
    } finally {
      setPreviewLoading(false);
    }
  };

  const exportBackup = async (revision: HomepageRevisionSummary) => {
    setBusy(true);
    setMessage(null);
    try {
      downloadBackupDocument(await client.exportBackup(revision.id));
      setMessage({
        tone: "success",
        text: `Backup for revision ${revision.revisionNumber} downloaded.`,
      });
    } catch (caught: unknown) {
      handleRequestFailure(caught);
    } finally {
      setBusy(false);
    }
  };

  const loadOlder = async () => {
    if (!history?.nextBeforeRevisionNumber || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const older = await client.loadHistory({
        beforeRevisionNumber: history.nextBeforeRevisionNumber,
      });
      setHistory(mergeHistory(history, older));
    } catch (caught: unknown) {
      handleRequestFailure(caught);
    } finally {
      setBusy(false);
    }
  };

  const confirmOperation = async () => {
    if (!confirmation || !contentState || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const next =
        confirmation.kind === "publish"
          ? await client.publishRevision(
              csrfToken,
              contentState.lockVersion,
              confirmation.revision.id,
            )
          : await client.restoreRevision(
              csrfToken,
              contentState.lockVersion,
              confirmation.revision.id,
            );
      setContentState(next);
      setDraft(
        next.latestRevision
          ? cloneHomepageContent(next.latestRevision.content)
          : null,
      );
      await reloadHistory();
      setMessage({
        tone: "success",
        text:
          confirmation.kind === "publish"
            ? `Revision ${confirmation.revision.revisionNumber} is now published.`
            : `Revision ${confirmation.revision.revisionNumber} was copied into new draft revision ${next.latestRevision?.revisionNumber ?? ""}. The public homepage was not changed.`,
      });
      setConfirmation(null);
      if (confirmation.kind === "restore") setPreview(null);
    } catch (caught: unknown) {
      handleRequestFailure(caught);
      setConfirmation(null);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <ContentState kind="loading" title="Loading the homepage editor">
        <p>
          Checking the current draft, published pointer, and revision history.
        </p>
      </ContentState>
    );
  }

  if (!contentState || !history) {
    return (
      <ContentState
        kind="error"
        title="The homepage editor could not be loaded"
        actions={
          <button type="button" onClick={() => void loadWorkspace()}>
            Try again
          </button>
        }
      >
        <p>{message?.text ?? "The secure content service is unavailable."}</p>
      </ContentState>
    );
  }

  const latest = contentState.latestRevision;
  const subject = session.data?.subject;
  const canCreate = Boolean(
    subject && canAccessPortalArea(subject, "admin-homepage", "create"),
  );
  const canWrite = Boolean(
    subject && canAccessPortalArea(subject, "admin-homepage", "write"),
  );
  const canApprove = Boolean(
    subject && canAccessPortalArea(subject, "admin-homepage", "publish"),
  );
  const canManage = Boolean(
    subject && canAccessPortalArea(subject, "admin-homepage", "manage"),
  );
  const canEditContent = canWrite || (!latest && canCreate);
  const canSave = canEditContent && dirty && !busy;
  const canPublish = Boolean(
    canApprove &&
      latest &&
      !dirty &&
      latest.id !== contentState.publishedRevisionId &&
      !busy,
  );

  return (
    <>
      <div className="homepage-editor__summary">
        <div>
          <p>
            Current version <b>{contentState.lockVersion}</b>
          </p>
          <p>
            {latest
              ? `Latest draft: revision ${latest.revisionNumber}`
              : "Live V2.1 baseline loaded; no private revision exists yet"}
          </p>
        </div>
        <div className="homepage-editor__summary-status">
          {contentState.publishedRevisionId ? (
            <StatusPill tone="positive">Published revision exists</StatusPill>
          ) : (
            <StatusPill tone="warning">Nothing published</StatusPill>
          )}
          {dirty && <StatusPill tone="warning">Unsaved edits</StatusPill>}
        </div>
      </div>

      <OperationFeedback
        message={message}
        onReload={() => void loadWorkspace()}
      />

      {draft ? (
        <form className="homepage-editor__form" onSubmit={saveDraft}>
          {!canEditContent && (
            <div className="homepage-editor__read-only" role="status">
              Read-only access: you can inspect sections, previews, and history,
              but your role cannot change or save homepage content.
            </div>
          )}
          <HomepageVisualEditor
            draft={draft}
            disabled={busy || !canEditContent}
            canCreateSections={canCreate && !busy}
            canManageSections={canManage && !busy}
            canManageTheme={canManage && !busy}
            onChange={(content) => {
              setDraft(content);
              setMessage(null);
            }}
          />
          <FormActions
            align="between"
            className="homepage-editor__sticky-actions"
          >
            <span aria-live="polite">
              {dirty ? "Unsaved edits" : "Draft matches the latest revision"}
            </span>
            <span className="homepage-editor__action-group">
              <button
                type="button"
                className="homepage-editor__secondary-button"
                disabled={busy || dirty || !latest}
                onClick={() => {
                  if (latest) void loadPreview(latest.id);
                }}
              >
                Preview latest
              </button>
              <button
                type="submit"
                className="homepage-editor__primary-button"
                disabled={!canSave}
              >
                {busy ? "Working…" : "Save new draft revision"}
              </button>
              <button
                type="button"
                className="homepage-editor__publish-button"
                disabled={!canPublish}
                onClick={() => {
                  if (latest) {
                    setConfirmation({ kind: "publish", revision: latest });
                  }
                }}
              >
                Publish latest revision
              </button>
            </span>
          </FormActions>
        </form>
      ) : null}

      <section
        className="homepage-editor__history"
        aria-labelledby="history-title"
      >
        <div className="homepage-editor__section-heading">
          <div>
            <p className="portal-kicker">Backups and rollback</p>
            <h2 id="history-title">Revision history</h2>
          </div>
          {previewLoading && <span role="status">Loading preview…</span>}
        </div>
        <p>
          Every save and restore is immutable. Restoring copies a reviewed
          revision into a new draft and never rewrites history.
        </p>
        <RevisionHistoryList
          history={history}
          busy={busy || previewLoading}
          canExport={canManage}
          onPreview={(revisionId) => void loadPreview(revisionId)}
          onExport={(revision) => void exportBackup(revision)}
          onLoadOlder={() => void loadOlder()}
        />
      </section>

      <Dialog
        id="homepage-revision-preview"
        className="homepage-editor__preview-dialog"
        open={preview !== null}
        title={
          preview ? `Preview revision ${preview.revisionNumber}` : "Preview"
        }
        description={
          preview
            ? `${formatHomepageRevisionDate(preview.createdAt)} · ${revisionEditorLabel(preview)}`
            : undefined
        }
        closeOnBackdrop={false}
        onRequestClose={() => {
          if (!busy) setPreview(null);
        }}
        actions={
          preview && (
            <>
              <button
                type="button"
                className="homepage-editor__secondary-button"
                disabled={busy || !canManage}
                onClick={() => void exportBackup(preview)}
              >
                Export backup
              </button>
              <button
                type="button"
                className="homepage-editor__restore-button"
                disabled={
                  busy || !canManage || preview.id === history.latestRevisionId
                }
                onClick={() =>
                  setConfirmation({ kind: "restore", revision: preview })
                }
              >
                Restore this reviewed revision
              </button>
            </>
          )
        }
      >
        {preview && <RevisionContentPreview revision={preview} />}
      </Dialog>

      <Dialog
        id="homepage-content-confirmation"
        open={confirmation !== null}
        title={
          confirmation?.kind === "publish"
            ? "Publish this revision?"
            : "Restore this revision?"
        }
        description="This action is recorded with your identity and timestamp."
        closeOnBackdrop={false}
        onRequestClose={() => {
          if (!busy) setConfirmation(null);
        }}
        actions={
          <>
            <button
              type="button"
              className="homepage-editor__secondary-button"
              data-dialog-initial-focus
              disabled={busy}
              onClick={() => setConfirmation(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={
                confirmation?.kind === "publish"
                  ? "homepage-editor__publish-button"
                  : "homepage-editor__restore-button"
              }
              disabled={busy}
              onClick={() => void confirmOperation()}
            >
              {busy
                ? "Working…"
                : confirmation?.kind === "publish"
                  ? "Confirm publish"
                  : "Confirm restore as new draft"}
            </button>
          </>
        }
      >
        {confirmation?.kind === "publish" ? (
          <p>
            Revision {confirmation.revision.revisionNumber} will replace the
            current public homepage content atomically.
          </p>
        ) : (
          <p>
            You reviewed revision {confirmation?.revision.revisionNumber}. A new
            restore revision will be created from that exact content. The public
            homepage will stay unchanged until the new draft is separately
            published.
          </p>
        )}
      </Dialog>
    </>
  );
}

function SessionState({
  session,
}: {
  readonly session: PortalSessionController;
}) {
  if (session.status === "loading") {
    return (
      <ContentState kind="loading" title="Checking secure access">
        <p>
          Confirming your application session and current homepage permission.
        </p>
      </ContentState>
    );
  }
  if (session.status === "authentication-required") {
    return (
      <ContentState kind="warning" title="Sign in is required">
        <p>{session.message}</p>
        <Suspense fallback={<p role="status">Loading secure sign-in…</p>}>
          <PortalFirebaseSignIn
            client={session.client}
            onSessionCreated={session.retry}
          />
        </Suspense>
      </ContentState>
    );
  }
  if (session.status === "access-denied") {
    return (
      <ContentState kind="error" title="Homepage access denied">
        <p>{session.message}</p>
      </ContentState>
    );
  }
  return (
    <ContentState
      kind="error"
      title="Secure access could not be checked"
      actions={
        <button type="button" onClick={session.retry}>
          Try again
        </button>
      }
    >
      <p>{session.message}</p>
      {session.correlationId && (
        <p>
          Support reference: <code>{session.correlationId}</code>
        </p>
      )}
    </ContentState>
  );
}

export function HomepageEditorRoute({
  session,
}: {
  readonly session: PortalSessionController;
}) {
  return (
    <div className="homepage-editor">
      <header className="homepage-editor__header">
        <p className="portal-kicker">Website administration</p>
        <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
          Homepage content
        </h1>
        <p>
          Edit approved visitor-facing copy by section. Saving creates a private
          revision; publishing is a separate confirmed action.
        </p>
      </header>
      {session.status === "authenticated" &&
      session.data &&
      canAccessPortalArea(session.data.subject, "admin-homepage", "view") ? (
        <AuthenticatedHomepageEditor session={session} />
      ) : session.status === "authenticated" ? (
        <ContentState kind="error" title="Homepage access denied">
          <p>Your role does not have permission to view Homepage.</p>
        </ContentState>
      ) : (
        <SessionState session={session} />
      )}
    </div>
  );
}

export function HomepageEditorRouteEntry() {
  const session = usePortalSession();
  return <HomepageEditorRoute session={session} />;
}
